/**
 * CODEX (doc 08 · Fish LinkedIn). Species you have actually killed, with their
 * exact numbers. Unmet species show as silhouettes — the list is the same
 * length either way, so an empty codex reads as a to-do list, not a bug.
 *
 * Without the Fish LinkedIn node the screen still opens, but the stat blocks
 * stay hidden: that node is what buys the numbers (and the live HP bars).
 */

import { S } from '../../content/strings.ts';
import { effectsOf } from '../../meta/effects.ts';
import { font } from '../../render/font/font.ts';
import { PixelSurface } from '../../render/surface.ts';
import { SPECIES, type SpeciesDef } from '../../sim/species.ts';
import type { FrameOpts, MenuItem } from '../../ui/menu.ts';
import { wrapText } from '../../ui/menu.ts';
import { g, UI } from '../../ui/theme.ts';
import { drawPanel, drawStatRow, type Rect } from '../../ui/widgets.ts';
import { MenuScreen } from './menuscreen.ts';
import { TitleScreen } from './title.ts';

const IDS = Object.keys(SPECIES);

export class CodexScreen extends MenuScreen {
  private met(id: string): boolean {
    return this.app.profile?.codex.includes(id) ?? false;
  }

  private get unlocked(): boolean {
    return effectsOf(this.app.profile?.owned ?? []).codex;
  }

  protected frame(): FrameOpts {
    const met = IDS.filter((id) => this.met(id)).length;
    return {
      title: S.codex.codex,
      subtitle: this.unlocked ? S.codex.endorsements : S.codex.fishLinkedinUnlocks,
      footer: S.codex.arrowsBrowseEsc,
      stamp: `${met} / ${IDS.length} MET`,
    };
  }

  protected override columnWidth(body: Rect): number {
    return Math.min(260, Math.floor(body.w * 0.4));
  }

  protected items(): MenuItem[] {
    return IDS.map((id) => {
      const sp = SPECIES[id]!;
      const met = this.met(id);
      return {
        label: met ? sp.name : '????????',
        value: met ? (sp.boss ? S.codex.boss : `${sp.hp} HP`) : S.codex.unmet,
        tone: sp.boss ? 'critical' : 'ink',
      };
    });
  }

  protected activate(): void {}

  protected override back(): void {
    this.app.go((a) => new TitleScreen(a));
  }

  protected override detail(s: PixelSurface, body: Rect): void {
    const id = IDS[this.selected];
    if (!id) return;
    const sp = SPECIES[id]!;
    const met = this.met(id);
    const x = body.x + this.columnWidth(body) + g(4);
    const w = Math.min(320, body.x + body.w - x);
    if (w < 150) return;
    const header = font('header');
    const bodyFont = font('body');
    const rows = met ? (this.unlocked ? statLines(sp).length : 2) : 2;
    const panelH = g(2) + header.capHeight + g(4) + rows * 12 + g(2);
    const panel: Rect = { x, y: body.y, w, h: Math.min(body.h, panelH) };
    drawPanel(s, panel, { raised: true });
    let y = panel.y + g(2);
    header.draw(s, met ? sp.name : S.codex.notMetYet, panel.x + g(2), y, met ? UI.ink : UI.inkDim, {
      caps: true,
      letterSpacing: 1,
    });
    y += header.capHeight + g(2);
    s.hline(panel.x + 2, panel.x + panel.w - 3, y, UI.panelRimLight);
    y += g(2);

    if (!met) {
      for (const line of wrapText('body', S.codex.killOneIts, panel.w - g(4))) {
        bodyFont.draw(s, line, panel.x + g(2), y, UI.inkDim, { caps: true });
        y += 11;
      }
      return;
    }
    if (!this.unlocked) {
      for (const line of wrapText('body', S.codex.metStatBlock, panel.w - g(4))) {
        bodyFont.draw(s, line, panel.x + g(2), y, UI.warning, { caps: true });
        y += 11;
      }
      return;
    }
    for (const [label, value] of statLines(sp)) {
      if (y + 12 > panel.y + panel.h) break; // a short window clips, never spills
      drawStatRow(s, { x: panel.x + g(2), y, w: panel.w - g(4), h: 8 }, label, value);
      y += 12;
    }
  }
}

/** The species' file, in the order a player asks the questions. */
function statLines(sp: SpeciesDef): Array<[string, string]> {
  const out: Array<[string, string]> = [
    ['HP', `${sp.hp}`],
    [S.codex.armor, `${sp.armor}`],
    [S.codex.speed, `${sp.speed.toFixed(2)} TILES/S`],
    [S.codex.chew, `${sp.dps} DPS`],
  ];
  if (sp.swift) out.push([S.codex.swift, S.codex.yesZappyLoves]);
  if (sp.boss) out.push([S.codex.role, sp.boss === 'boss' ? S.codex.boss2 : S.codex.miniBoss]);
  if (sp.behavior === 'saboteur') out.push([S.codex.behaviour, S.codex.huntsEconomy]);
  if (sp.grab) out.push([S.codex.grab, `DISABLES A TOWER ${sp.grab.disableSeconds}S EVERY ${sp.grab.everySeconds}S`]);
  if (sp.escort) out.push([S.codex.escorts, `CALLS FRIENDS EVERY ${sp.escort.everySeconds}S`]);
  if (sp.enrage) {
    out.push([S.codex.enrage, `x${sp.enrage.speedMult} SPEED UNDER ${Math.round(sp.enrage.belowHpFraction * 100)}% HP`]);
  }
  return out;
}
